import { AxiosResponse } from "axios";
import {
  ServerListResponse,
  Comic,
  Artist,
  Release,
  Bundle,
  LayoutStyle,
  BrowseTabType,
  Article,
} from "../../../types";
import { useMemo } from "react";
import { InfiniteData } from "@tanstack/react-query";
import { BundleResultsGrid } from "../../../components/resultsGrid/BundleResultsGrid";
import { FluidVirtualGrid } from "../../../components/resultsGrid/FluidVirtualGrid";
import { artistResultsCardV2 } from "../../../components/resultsGrid/ArtistsResultsCard";
import {
  comicsResultsCardV2,
  releasesResultsCardV2,
  bundlesResultsCard,
} from "../../../components/resultsGrid/ComicsResultsCard";
import { useEntityCache } from "../../../hooks/useEntityCache";
import { sizeEstimator } from "../../../components/cards/hooks/useGridCardDimensions";

interface BrowseResultsGridProps {
  tab: BrowseTabType;
  mode: LayoutStyle["mode"];
  onScroll?: (scrollableRef: HTMLDivElement) => void;
  gridHeightAdjustment?: string;
  data:
    | InfiniteData<AxiosResponse<ServerListResponse<Comic>, any>, unknown>
    | InfiniteData<AxiosResponse<ServerListResponse<Release>, any>, unknown>
    | InfiniteData<AxiosResponse<ServerListResponse<Artist>, any>, unknown>
    | InfiniteData<AxiosResponse<ServerListResponse<Bundle>, any>, unknown>
    | undefined;
  urlSettled: boolean;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  header?: React.ReactNode;
  /** Callback for scroll restoration - reports first visible index */
  onVisibleIndexChange?: (index: number) => void;
  /** Index to restore scroll to on back navigation */
  restoreToIndex?: number;
  /** Whether the user is actively searching */
  isSearching?: boolean;
}
export function BrowseResultsGrid({
  tab,
  mode,
  onScroll,
  gridHeightAdjustment,
  data,
  urlSettled,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  header,
  onVisibleIndexChange,
  restoreToIndex,
  isSearching,
}: BrowseResultsGridProps) {
  const { saveEntityToCache } = useEntityCache();

  const { allPages, fallbackMessage, searchQuery } = useMemo(() => {
    let extractedMessage: string | null = null;

    const processedPages = data?.pages.map((page) => {
      // If there are no results but suggestions exist, use the fallback results
      if (
        page.data.payload.results.length === 0 &&
        page.data.payload.suggestions?.search
      ) {
        const search = page.data.payload.suggestions.search;
        let fallbackResults: (Comic | Artist | Release)[] = [];

        // Extract fallback results and message based on tab type
        switch (tab) {
          case BrowseTabType.comics:
            fallbackResults = search.comics?.results ?? [];
            extractedMessage = search.comics?.message ?? null;
            break;
          case BrowseTabType.releases:
            fallbackResults = search.releases?.results ?? [];
            extractedMessage = search.releases?.message ?? null;
            break;
          case BrowseTabType.creators:
            fallbackResults = search.artists?.results ?? [];
            extractedMessage = search.artists?.message ?? null;
            break;
        }

        // Cache fallback results
        fallbackResults.forEach((n) => saveEntityToCache(n));

        // Return a modified page with fallback results
        return {
          ...page,
          data: {
            ...page.data,
            payload: {
              ...page.data.payload,
              results: fallbackResults,
            },
          },
        };
      }

      // Normal case: cache and return original results
      page.data.payload.results.forEach((n) => saveEntityToCache(n));
      return page;
    });

    return {
      allPages: (processedPages ?? []) as AxiosResponse<
        ServerListResponse<Comic | Artist | Article | Release | Bundle>
      >[],
      fallbackMessage: extractedMessage,
      searchQuery: data?.pages[0]?.data.payload.filters.q ?? null,
    };
  }, [data, tab, saveEntityToCache]);

  const v2Layout = useMemo(() => {
    switch (tab) {
      case BrowseTabType.comics:
        return comicsResultsCardV2(mode, !!isSearching);
      case BrowseTabType.releases:
        return releasesResultsCardV2(mode, !!isSearching);
      case BrowseTabType.creators:
        return artistResultsCardV2(mode);
      default:
        return null;
    }
  }, [tab, mode, isSearching]);

  // Use BundleResultsGrid for bundles tab
  if (tab === BrowseTabType.bundles) {
    return (
      <BundleResultsGrid
        gridKey={`${mode}-${tab}`}
        header={header}
        gridHeightAdjustment={gridHeightAdjustment ?? "325px"}
        layout={bundlesResultsCard(mode)}
        isLoading={!urlSettled || isLoading}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        allPages={allPages as AxiosResponse<ServerListResponse<Bundle>>[]}
        onScroll={onScroll}
      />
    );
  }

  // Use FluidVirtualGrid with V2 card styling
  if (v2Layout) {
    const dims = sizeEstimator(
      v2Layout.style.context,
      v2Layout.style.mode,
      v2Layout.style.size,
    );
    return (
      <FluidVirtualGrid
        gridKey={`v2-${mode}-${tab}`}
        header={header}
        gridHeightAdjustment={gridHeightAdjustment ?? "325px"}
        layout={v2Layout}
        minCardWidth={mode === "list" ? 360 : dims.width}
        minColumns={mode === "list" ? 1 : 2}
        gap={mode === "list" ? 8 : 24}
        isLoading={!urlSettled || isLoading}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        allPages={allPages}
        onScroll={onScroll}
        fallbackMessage={fallbackMessage}
        searchQuery={searchQuery ?? undefined}
        onVisibleIndexChange={onVisibleIndexChange}
        restoreToIndex={restoreToIndex}
      />
    );
  }

  return null;
}
